import { useState } from 'react';
import { useDeals, useUpdateDeal, useCreateDeal, useContacts } from '@/hooks/useCRM';
import PageHeader from '@/components/shared/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, DollarSign } from 'lucide-react';
import { format } from 'date-fns';
import LeadScoreBadge from '@/components/crm/LeadScoreBadge';
import type { DealStage, Deal } from '@/types/models';
import { toast } from 'sonner';

const STAGES: DealStage[] = ['lead', 'qualified', 'proposal', 'negotiation', 'won', 'lost'];
const STAGE_COLORS: Record<DealStage, string> = {
  lead: 'border-muted-foreground/30',
  qualified: 'border-blue-500/50',
  proposal: 'border-amber-500/50',
  negotiation: 'border-purple-500/50',
  won: 'border-green-500/50',
  lost: 'border-destructive/50',
};

export default function DealsPage() {
  const { data: deals = [] } = useDeals();
  const updateDeal = useUpdateDeal();
  const createDeal = useCreateDeal();
  const { data: contacts = [] } = useContacts();
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ title: '', value: '', stage: 'lead' as DealStage, customer_id: '', contact_id: '', expected_close_date: '', notes: '' });

  const dealsByStage = STAGES.reduce((acc, s) => {
    acc[s] = deals.filter(d => d.stage === s);
    return acc;
  }, {} as Record<DealStage, Deal[]>);

  const handleDrop = (dealId: string, newStage: DealStage) => {
    updateDeal.mutate({ id: dealId, data: { stage: newStage } });
  };

  const handleCreate = () => {
    if (!form.title || !form.value || !form.stage) {
      toast.error('Title, value, and stage are required');
      return;
    }
    createDeal.mutate(
      { ...form, value: Number(form.value) },
      {
        onSuccess: () => {
          toast.success('Deal created');
          setCreateOpen(false);
          setForm({ title: '', value: '', stage: 'lead', customer_id: '', contact_id: '', expected_close_date: '', notes: '' });
        },
        onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to create deal'),
      },
    );
  };

  return (
    <div className="space-y-6 p-6">
      <PageHeader title="Deals Pipeline" subtitle="Track opportunities through your sales pipeline">
        <Button className="gap-2" onClick={() => setCreateOpen(true)}><Plus className="h-4 w-4" /> New Deal</Button>
      </PageHeader>

      <div className="grid gap-4 lg:grid-cols-6">
        {STAGES.map(stage => (
          <div key={stage} className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold capitalize">{stage}</h3>
              <Badge variant="secondary" className="text-[10px]">{dealsByStage[stage].length}</Badge>
            </div>
            <div className="space-y-2">
              {dealsByStage[stage].map(deal => (
                <Card key={deal.id} className={`border-l-4 ${STAGE_COLORS[stage]}`}>
                  <CardContent className="space-y-2 p-3">
                    <p className="text-sm font-medium leading-tight">{deal.title}</p>
                    <p className="text-xs text-muted-foreground">{deal.customer_name}</p>
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1 text-xs font-semibold">
                        <DollarSign className="h-3 w-3" />{deal.value.toLocaleString()}
                      </span>
                      <LeadScoreBadge score={deal.lead_score} />
                    </div>
                    {deal.expected_close_date && (
                      <p className="text-[10px] text-muted-foreground">
                        Close: {format(new Date(deal.expected_close_date), 'MMM d')}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
              {dealsByStage[stage].length === 0 && (
                <div className="rounded-md border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
                  No deals
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New Deal</DialogTitle>
            <DialogDescription>Create a new deal in your pipeline.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Title *</Label>
              <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Q2 Laptop Refresh — 200 units" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Value ($) *</Label>
                <Input type="number" value={form.value} onChange={e => setForm(p => ({ ...p, value: e.target.value }))} placeholder="50000" />
              </div>
              <div className="space-y-1">
                <Label>Stage *</Label>
                <Select value={form.stage} onValueChange={v => setForm(p => ({ ...p, stage: v as DealStage }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STAGES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Contact</Label>
              <Select value={form.contact_id} onValueChange={v => {
                const c = contacts.find(ct => ct.id === v);
                setForm(p => ({ ...p, contact_id: v, customer_id: c?.customer_id || p.customer_id }));
              }}>
                <SelectTrigger><SelectValue placeholder="Select contact" /></SelectTrigger>
                <SelectContent>
                  {contacts.map(c => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Customer ID</Label>
              <Input value={form.customer_id} onChange={e => setForm(p => ({ ...p, customer_id: e.target.value }))} placeholder="UUID of the customer" />
            </div>
            <div className="space-y-1">
              <Label>Expected Close Date</Label>
              <Input type="date" value={form.expected_close_date} onChange={e => setForm(p => ({ ...p, expected_close_date: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Additional details" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={createDeal.isPending}>Create Deal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
